import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Auth Pages
import Login from './components/auth/Login';
import AdminLogin from './components/auth/AdminLogin';
import Register from './components/auth/Register';
import ResetPassword from './components/auth/ResetPassword';
import AdminInit from './components/admin/AdminInit'; // Import AdminInit component

// Customer Pages
import Dashboard from './components/dashboard/Dashboard';
import Projects from './components/projects/Projects';
import ProjectDetail from './components/projects/ProjectDetail';
import CreateProject from './components/projects/CreateProject';
import Clients from './components/clients/Clients';
import ClientDetail from './components/clients/ClientDetail';
import CreateClient from './components/clients/CreateClient';
import Documents from './components/documents/Documents';
import Employees from './components/employees/Employees';
import EmployeeDetail from './components/employees/EmployeeDetail';
import CreateEmployee from './components/employees/CreateEmployee';
import EditEmployee from './components/employees/EditEmployee';
import TimeEntries from './components/time-entries/TimeEntries';
import AssignEmployees from './components/projects/AssignEmployees';
import PaymentSchedule from './components/projects/payment-schedule/PaymentSchedule';
// Import the new Reports component
import ReportsPage from './components/reports/ReportsPage';
import Payments from './components/payments/Payments';
import CreatePayment from './components/payments/CreatePayment';
import EditPayment from './components/payments/EditPayment';
import PaymentTracking from './components/payments/PaymentTracking';
import ProjectPaymentDetails from './components/payments/ProjectPaymentDetails';
import LicenseManagement from './components/license/LicenseManagement';
import SigningPage from './components/signing/SigningPage';

// Admin Pages
import AdminDashboard from './components/admin/AdminDashboard';
import CustomersList from './components/admin/CustomersList';
import CreateCustomer from './components/admin/CreateCustomer';
import UsersList from './components/admin/UsersList';
import CreateUser from './components/admin/CreateUser';

// Context
import AuthContext from './context/AuthContext';
import { LicenseProvider } from './context/LicenseContext';
import PrivateRoute from './components/routing/PrivateRoute';
import AdminRoute from './components/routing/AdminRoute';

// License Components
import ReadOnlyBanner from './components/license/ReadOnlyBanner';
import LicenseWarning from './components/license/LicenseWarning';

function App() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Check if user is authenticated
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await fetch('/api/auth/user', {
            headers: {
              'x-auth-token': token
            }
          });
          
          const data = await response.json();
          
          if (response.ok) {
            console.log('User data from /api/auth/user:', data);
            console.log('Is admin?', data.isAdmin);
            setUser(data);
            setIsAuthenticated(true);
          } else {
            // Token is invalid or expired
            setToken(null);
            localStorage.removeItem('token');
            setIsAuthenticated(false);
            setUser(null);
          }
        } catch (err) {
          console.error('Error checking authentication', err);
          setToken(null);
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          setUser(null);
        }
      }
      
      setIsLoading(false);
    };
    
    checkAuth();
  }, [token]);
  
  // Login user
  const login = (newToken, newUser) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    if (newUser) {
      setUser(newUser);
    }
    setIsAuthenticated(true);
  };
  
  // Logout user
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      token,
      login,
      logout
    }}>
      <LicenseProvider>
        <Router>
          <div className="App d-flex flex-column min-vh-100">
            <Header />
            <main className="flex-grow-1 my-3">
              <div className="container">
                {isAuthenticated && (
                  <>
                    <ReadOnlyBanner />
                    <LicenseWarning />
                  </>
                )}
                <Routes>
                <Route path="/admin/init" element={<AdminInit />} />
                {/* Public Routes */}
                <Route path="/" element={isAuthenticated ? (
                  user?.isAdmin ? <Navigate to="/admin/dashboard" /> : <Navigate to="/dashboard" />
                ) : <Login />} />
                <Route path="/login" element={<Login />} />
                <Route path="/admin" element={<AdminLogin />} />
                <Route path="/register" element={<Register />} />
                <Route path="/reset-password/:customer/:token" element={<ResetPassword />} />
                
                {/* Public Signing Route - No authentication required */}
                <Route path="/sign/:token" element={<SigningPage />} />
                
                {/* Customer Routes */}
                <Route 
                  path="/dashboard" 
                  element={
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/projects" 
                  element={
                    <PrivateRoute>
                      <Projects />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/projects/:id" 
                  element={
                    <PrivateRoute>
                      <ProjectDetail />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/projects/create" 
                  element={
                    <PrivateRoute>
                      <CreateProject />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/clients" 
                  element={
                    <PrivateRoute>
                      <Clients />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/clients/:id" 
                  element={
                    <PrivateRoute>
                      <ClientDetail />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/clients/create" 
                  element={
                    <PrivateRoute>
                      <CreateClient />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/documents" 
                  element={
                    <PrivateRoute>
                      <Documents />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/employees" 
                  element={
                    <PrivateRoute>
                      <Employees />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/employees/:id" 
                  element={
                    <PrivateRoute>
                      <EmployeeDetail />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/employees/:id/edit" 
                  element={
                    <PrivateRoute>
                      <EditEmployee />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/employees/create" 
                  element={
                    <PrivateRoute>
                      <CreateEmployee />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/projects/:projectId/time-entries" 
                  element={
                    <PrivateRoute>
                      <TimeEntries />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/projects/:projectId/assign-employees" 
                  element={
                    <PrivateRoute>
                      <AssignEmployees />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/projects/:projectId/payment-schedule" 
                  element={
                    <PrivateRoute>
                      <PaymentSchedule />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/reports" 
                  element={
                    <PrivateRoute>
                      <ReportsPage />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/payments" 
                  element={
                    <PrivateRoute>
                      <Payments />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/payments/:projectId" 
                  element={
                    <PrivateRoute>
                      <ProjectPaymentDetails />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/payments/create" 
                  element={
                    <PrivateRoute>
                      <CreatePayment />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/payments/edit/:id" 
                  element={
                    <PrivateRoute>
                      <EditPayment />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/license" 
                  element={
                    <PrivateRoute>
                      <LicenseManagement />
                    </PrivateRoute>
                  } 
                />
                
                {/* Admin Routes */}
                <Route 
                  path="/admin/dashboard" 
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/customers" 
                  element={
                    <AdminRoute>
                      <CustomersList />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/customers/create" 
                  element={
                    <AdminRoute>
                      <CreateCustomer />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/users" 
                  element={
                    <AdminRoute>
                      <UsersList />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/users/create" 
                  element={
                    <AdminRoute>
                      <CreateUser />
                    </AdminRoute>
                  } 
                />
                </Routes>
              </div>
            </main>
            <Footer />
          </div>
        </Router>
      </LicenseProvider>
    </AuthContext.Provider>
  );
}

export default App;
